import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, Trash2, Loader2, RefreshCw, Calendar } from "lucide-react";
import { API } from "../../contexts/AuthContext";

export default function ContactRequests() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/contact-requests`);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error("Eroare la încărcare");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onDelete = async (id) => {
    if (!window.confirm("Ștergeți această cerere?")) return;
    try {
      await axios.delete(`${API}/admin/contact-requests/${id}`);
      toast.success("Șters");
      load();
    } catch {
      toast.error("Eroare la ștergere");
    }
  };

  return (
    <div data-testid="admin-contact-requests">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="overline mb-1">Cereri primite</div>
          <div className="font-mono-tech text-xs text-neutral-500">
            {rows.length} cere{rows.length === 1 ? "re" : "ri"}
          </div>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-2 btn-outline px-4 py-2.5 text-xs font-mono-tech uppercase tracking-[0.15em]"
        >
          <RefreshCw className="h-4 w-4" /> Reîncarcă
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-neutral-500">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : rows.length === 0 ? (
        <div className="border border-neutral-800 p-10 text-center text-neutral-500 font-mono-tech text-sm">
          Nicio cerere încă.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {rows.map((r) => (
            <article
              key={r.id}
              data-testid={`contact-row-${r.id}`}
              className="bg-[#0d0d0d] border border-neutral-800 p-5"
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="font-display font-semibold text-white text-lg">{r.name}</div>
                <div className="flex items-center gap-1 font-mono-tech text-[10px] uppercase tracking-[0.15em] text-neutral-500">
                  <Calendar className="h-3 w-3" />
                  {new Date(r.created_at).toLocaleDateString("ro-RO", {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })}
                </div>
              </div>
              {r.service && (
                <div className="inline-block font-mono-tech text-[10px] uppercase tracking-[0.15em] px-2 py-1 border border-yellow-500/30 text-yellow-400 mb-3">
                  {r.service}
                </div>
              )}
              <div className="space-y-1.5 text-sm">
                <a
                  href={`mailto:${r.email}`}
                  className="flex items-center gap-2 text-neutral-300 hover:text-yellow-400 break-all"
                >
                  <Mail className="h-3.5 w-3.5 text-yellow-400 shrink-0" />
                  {r.email}
                </a>
                {r.phone && (
                  <a
                    href={`tel:${r.phone}`}
                    className="flex items-center gap-2 text-neutral-300 hover:text-yellow-400"
                  >
                    <Phone className="h-3.5 w-3.5 text-yellow-400 shrink-0" />
                    {r.phone}
                  </a>
                )}
              </div>
              {r.subject && (
                <div className="mt-3 font-display text-white text-sm">{r.subject}</div>
              )}
              <p className="mt-3 text-sm text-neutral-400 whitespace-pre-wrap leading-relaxed">
                {r.message}
              </p>
              <div className="mt-4 pt-3 border-t border-neutral-900 flex items-center justify-end">
                <button
                  onClick={() => onDelete(r.id)}
                  className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-red-400 font-mono-tech uppercase tracking-[0.15em]"
                >
                  <Trash2 className="h-3 w-3" /> Șterge
                </button>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
